import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Icon } from "@/components/ui/icon";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface SeoEntity {
  id: string;
  name: string;
  type: string;
  slug: string;
  description?: string;
  image_url?: string;
  live_demo_url?: string;
}

interface SeoOverride {
  id?: string;
  entity_type: string;
  entity_id: string | null;
  slug: string | null;
  seo_title: string;
  seo_description: string;
  canonical_url: string;
  index: boolean;
  follow: boolean;
  og_title: string;
  og_description: string;
  og_image_url: string;
  twitter_title: string;
  twitter_description: string;
  twitter_image_url: string;
  structured_data_type: string;
}

interface ProjectImage {
  id: string;
  project_id: string;
  image_url: string;
  display_order: number;
}

const emptyOverride: SeoOverride = {
  entity_type: '',
  entity_id: null,
  slug: null,
  seo_title: '',
  seo_description: '',
  canonical_url: '',
  index: true,
  follow: true,
  og_title: '',
  og_description: '',
  og_image_url: '',
  twitter_title: '',
  twitter_description: '',
  twitter_image_url: '',
  structured_data_type: '',
};

// Component to show inherited value badge
const InheritedBadge = ({ source }: { source: string }) => (
  <Badge variant="outline" className="text-xs font-normal ml-2">
    <Icon name="link" size={10} className="mr-1" />
    From {source}
  </Badge>
);

export const SeoOverridesTab = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedEntity, setSelectedEntity] = useState<SeoEntity | null>(null);
  const [override, setOverride] = useState<SeoOverride>(emptyOverride);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Fetch projects with all relevant data
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects-for-seo'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, project_name, description, image_url, live_demo_url')
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });

  // Fetch project images to get cover images
  const { data: projectImages } = useQuery({
    queryKey: ['project-images-for-seo'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_images')
        .select('id, project_id, image_url, display_order')
        .order('display_order');
      if (error) throw error;
      return data as ProjectImage[];
    },
  });

  // Fetch existing overrides
  const { data: overrides, isLoading: overridesLoading } = useQuery({
    queryKey: ['seo-overrides-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_overrides')
        .select('*');
      if (error) throw error;
      return data;
    },
  });

  const isLoading = projectsLoading || overridesLoading;

  // Get cover image for a project (first image by display_order)
  const getCoverImage = (projectId: string): string | undefined => {
    const images = projectImages?.filter(img => img.project_id === projectId) || [];
    if (images.length === 0) return undefined;
    const sorted = [...images].sort((a, b) => a.display_order - b.display_order);
    return sorted[0]?.image_url;
  };

  // Build entities list with full project data
  const entities: SeoEntity[] = [
    { id: 'resume', name: 'Resume', type: 'resume', slug: '/' },
    ...(projects?.map(p => ({
      id: p.id,
      name: p.project_name,
      type: 'project',
      slug: `/projects/${p.id}`,
      description: p.description || undefined,
      image_url: getCoverImage(p.id) || p.image_url || undefined,
      live_demo_url: p.live_demo_url || undefined,
    })) || []),
  ];

  // Filter entities
  const filteredEntities = entities.filter(e => {
    const matchesSearch = e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.slug.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || e.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: SeoOverride) => {
      const payload = {
        entity_type: data.entity_type,
        entity_id: data.entity_id,
        slug: data.slug,
        seo_title: data.seo_title || null,
        seo_description: data.seo_description || null,
        canonical_url: data.canonical_url || null,
        index: data.index,
        follow: data.follow,
        og_title: data.og_title || null,
        og_description: data.og_description || null,
        og_image_url: data.og_image_url || null,
        twitter_title: data.twitter_title || null,
        twitter_description: data.twitter_description || null,
        twitter_image_url: data.twitter_image_url || null,
        structured_data_type: data.structured_data_type || null,
      };

      if (data.id) {
        const { error } = await supabase
          .from('seo_overrides')
          .update(payload)
          .eq('id', data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('seo_overrides')
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-overrides-all'] });
      queryClient.invalidateQueries({ queryKey: ['seo-overrides'] });
      toast.success('SEO override saved');
      setSheetOpen(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  const handleSelectEntity = (entity: SeoEntity) => {
    setSelectedEntity(entity);
    
    // Find existing override
    const existingOverride = overrides?.find(o => 
      (o.entity_id === entity.id) || 
      (entity.type === 'resume' && o.entity_type === 'resume')
    );

    if (existingOverride) {
      setOverride({
        id: existingOverride.id,
        entity_type: existingOverride.entity_type,
        entity_id: existingOverride.entity_id,
        slug: existingOverride.slug,
        seo_title: existingOverride.seo_title || '',
        seo_description: existingOverride.seo_description || '',
        canonical_url: existingOverride.canonical_url || '',
        index: existingOverride.index ?? true,
        follow: existingOverride.follow ?? true,
        og_title: existingOverride.og_title || '',
        og_description: existingOverride.og_description || '',
        og_image_url: existingOverride.og_image_url || '',
        twitter_title: existingOverride.twitter_title || '',
        twitter_description: existingOverride.twitter_description || '',
        twitter_image_url: existingOverride.twitter_image_url || '',
        structured_data_type: existingOverride.structured_data_type || '',
      });
    } else {
      setOverride({
        ...emptyOverride,
        entity_type: entity.type,
        entity_id: entity.type === 'resume' ? null : entity.id,
        slug: entity.slug,
      });
    }
    
    setSheetOpen(true);
  };

  const handleSave = () => {
    saveMutation.mutate(override);
  };

  const hasOverride = (entityId: string) => {
    return overrides?.some(o => o.entity_id === entityId || (entityId === 'resume' && o.entity_type === 'resume'));
  };

  // Check if a field is using inherited value from project
  const isInheritedFromProject = (field: keyof SeoOverride) => {
    if (!selectedEntity || selectedEntity.type !== 'project') return false;
    return !override[field];
  };

  // Get placeholder/inherited value from project
  const getProjectPlaceholder = (field: 'title' | 'description' | 'image' | 'canonical') => {
    if (!selectedEntity || selectedEntity.type !== 'project') return '';
    switch (field) {
      case 'title': return selectedEntity.name;
      case 'description': return selectedEntity.description || '';
      case 'image': return selectedEntity.image_url || '';
      case 'canonical': return selectedEntity.live_demo_url || '';
      default: return '';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by title or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="resume">Resume</SelectItem>
            <SelectItem value="project">Projects</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Entity List */}
      <div className="space-y-2">
        {filteredEntities.map((entity) => (
          <Card 
            key={entity.id}
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => handleSelectEntity(entity)}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded bg-muted flex items-center justify-center overflow-hidden">
                  {entity.image_url ? (
                    <img src={entity.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Icon 
                      name={entity.type === 'resume' ? 'person' : 'folder'} 
                      size={20} 
                      className="text-muted-foreground" 
                    />
                  )}
                </div>
                <div>
                  <p className="font-medium text-foreground">{entity.name}</p>
                  <p className="text-sm text-muted-foreground">{entity.slug}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{entity.type}</Badge>
                {hasOverride(entity.id) && (
                  <Badge variant="default" className="bg-primary/10 text-primary">
                    <Icon name="edit" size={12} className="mr-1" />
                    Customized
                  </Badge>
                )}
                <Icon name="chevron_right" size={20} className="text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Override Editor Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit SEO for {selectedEntity?.name}</SheetTitle>
            <SheetDescription>
              {selectedEntity?.type === 'project' 
                ? 'Empty fields will inherit from project data' 
                : 'Override default SEO settings for this page'}
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-180px)] pr-4 mt-6">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic</TabsTrigger>
                <TabsTrigger value="social">Social</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label>SEO Title</Label>
                    {isInheritedFromProject('seo_title') && <InheritedBadge source="Project" />}
                  </div>
                  <Input
                    value={override.seo_title}
                    onChange={(e) => setOverride({ ...override, seo_title: e.target.value })}
                    placeholder={getProjectPlaceholder('title') || "Custom page title"}
                  />
                  <p className="text-xs text-muted-foreground">
                    {(override.seo_title || getProjectPlaceholder('title')).length}/60 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label>SEO Description</Label>
                    {isInheritedFromProject('seo_description') && <InheritedBadge source="Project" />}
                  </div>
                  <Textarea
                    value={override.seo_description}
                    onChange={(e) => setOverride({ ...override, seo_description: e.target.value })}
                    placeholder={getProjectPlaceholder('description') || "Custom meta description"}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    {(override.seo_description || getProjectPlaceholder('description')).length}/160 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Label>Canonical URL</Label>
                    {isInheritedFromProject('canonical_url') && selectedEntity?.live_demo_url && <InheritedBadge source="Project" />}
                  </div>
                  <Input
                    value={override.canonical_url}
                    onChange={(e) => setOverride({ ...override, canonical_url: e.target.value })}
                    placeholder={getProjectPlaceholder('canonical') || "https://example.com/page"}
                  />
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="index-toggle">Allow search engine indexing</Label>
                    <Switch
                      id="index-toggle"
                      checked={override.index}
                      onCheckedChange={(checked) => setOverride({ ...override, index: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="follow-toggle">Allow following links</Label>
                    <Switch
                      id="follow-toggle"
                      checked={override.follow}
                      onCheckedChange={(checked) => setOverride({ ...override, follow: checked })}
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Structured Data Type</Label>
                  <Select
                    value={override.structured_data_type}
                    onValueChange={(value) => setOverride({ ...override, structured_data_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="None">None</SelectItem>
                      <SelectItem value="Project">Project</SelectItem>
                      <SelectItem value="Article">Article</SelectItem>
                      <SelectItem value="CreativeWork">CreativeWork</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="social" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">OpenGraph</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label>OG Title</Label>
                        {isInheritedFromProject('og_title') && <InheritedBadge source="Project" />}
                      </div>
                      <Input
                        value={override.og_title}
                        onChange={(e) => setOverride({ ...override, og_title: e.target.value })}
                        placeholder={getProjectPlaceholder('title') || "Social share title"}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label>OG Description</Label>
                        {isInheritedFromProject('og_description') && <InheritedBadge source="Project" />}
                      </div>
                      <Textarea
                        value={override.og_description}
                        onChange={(e) => setOverride({ ...override, og_description: e.target.value })}
                        placeholder={getProjectPlaceholder('description') || "Social share description"}
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label>OG Image URL</Label>
                        {isInheritedFromProject('og_image_url') && selectedEntity?.image_url && <InheritedBadge source="Project" />}
                      </div>
                      <Input
                        value={override.og_image_url}
                        onChange={(e) => setOverride({ ...override, og_image_url: e.target.value })}
                        placeholder={getProjectPlaceholder('image') || "https://example.com/image.jpg"}
                      />
                      {(override.og_image_url || getProjectPlaceholder('image')) && (
                        <div className="mt-2 rounded border overflow-hidden">
                          <img 
                            src={override.og_image_url || getProjectPlaceholder('image')} 
                            alt="OG Preview" 
                            className="w-full h-32 object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Twitter</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label>Twitter Title</Label>
                        {isInheritedFromProject('twitter_title') && <InheritedBadge source="Project" />}
                      </div>
                      <Input
                        value={override.twitter_title}
                        onChange={(e) => setOverride({ ...override, twitter_title: e.target.value })}
                        placeholder={getProjectPlaceholder('title') || "Twitter card title"}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label>Twitter Description</Label>
                        {isInheritedFromProject('twitter_description') && <InheritedBadge source="Project" />}
                      </div>
                      <Textarea
                        value={override.twitter_description}
                        onChange={(e) => setOverride({ ...override, twitter_description: e.target.value })}
                        placeholder={getProjectPlaceholder('description') || "Twitter card description"}
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label>Twitter Image URL</Label>
                        {isInheritedFromProject('twitter_image_url') && selectedEntity?.image_url && <InheritedBadge source="Project" />}
                      </div>
                      <Input
                        value={override.twitter_image_url}
                        onChange={(e) => setOverride({ ...override, twitter_image_url: e.target.value })}
                        placeholder={getProjectPlaceholder('image') || "https://example.com/twitter-image.jpg"}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="preview" className="space-y-4 mt-4">
                {/* Google Preview */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Icon name="search" size={16} />
                      Google Search Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      <p className="text-blue-600 text-lg hover:underline cursor-pointer line-clamp-1">
                        {override.seo_title || getProjectPlaceholder('title') || selectedEntity?.name || 'Page Title'}
                      </p>
                      <p className="text-green-700 text-sm">
                        {selectedEntity?.slug || '/'}
                      </p>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {override.seo_description || getProjectPlaceholder('description') || 'No description set'}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Social Card Preview */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Icon name="share" size={16} />
                      Social Card Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      {(override.og_image_url || getProjectPlaceholder('image')) && (
                        <img 
                          src={override.og_image_url || getProjectPlaceholder('image')} 
                          alt="Social preview" 
                          className="w-full h-40 object-cover"
                        />
                      )}
                      <div className="p-3 bg-muted/30">
                        <p className="font-medium line-clamp-1">
                          {override.og_title || override.seo_title || getProjectPlaceholder('title') || selectedEntity?.name}
                        </p>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {override.og_description || override.seo_description || getProjectPlaceholder('description')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="mt-6 pt-4 border-t">
              <Button 
                onClick={handleSave} 
                disabled={saveMutation.isPending}
                className="w-full"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Override'}
              </Button>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
};
